import {
  $inputRule,
  $nodeSchema,
  $prose,
  $remark,
  $view,
} from '@milkdown/utils'
import { remarkStringifyOptionsCtx } from '@milkdown/core'
import type { Editor } from '@milkdown/core'
import type { Ctx } from '@milkdown/ctx'
import type { MarkdownNode } from '@milkdown/transformer'
import { InputRule } from '@milkdown/prose/inputrules'
import { keymap } from '@milkdown/prose/keymap'
import { TextSelection } from '@milkdown/prose/state'
import type { Command, EditorState, Transaction } from '@milkdown/prose/state'
import type { NodeViewConstructor } from '@milkdown/prose/view'
import type { NodeType, Node as ProseNode } from '@milkdown/prose/model'
import type { Options as RemarkStringifyOptions } from 'remark-stringify'
import {
  createInputRuleRegex,
  createTokenRegex,
  isDelimiterShell,
  opensAsFact,
} from './tokenRegex'
import { jinjaTokenNormalizer } from './jinjaNormalizer'
import { snippetBlockPlugins } from '../snippet/snippetBlock'
import { snippetBlockStringifyHandlers } from '../snippet/snippetRemark'
import {
  snippetBlockViewConfig,
  type SnippetBlockViewConfig,
} from '../snippet/snippetReferenceSource'
import { cn } from '@/lib/utils'

/**
 * Adds the kind-specific modifier class (fact vs jinja) that the CSS colors key
 * off (colors in index.css). `opensAsFact` classifies by the OPENING delimiter,
 * so an empty single-brace shell (`{ }`, `{}`) still colors blue while it's
 * being filled.
 */
function jinjaTokenClass(value: string): string {
  return cn(
    'jinja-token',
    opensAsFact(value) ? 'jinja-token--fact' : 'jinja-token--jinja'
  )
}

/**
 * `jinjaToken` — a first-class INLINE CONTENT node for Jinja/template tokens
 * (`{{ expr }}`, `{# cmt #}`, `{% stmt %}`, `{single_brace}`). The token's full
 * text — delimiters included — IS the node's text content, so editing a token
 * feels like editing normal prose (no swapped-in input). Marks are banned INSIDE
 * the token (`marks: ''`), and the text is emitted VERBATIM on save, never
 * re-escaped (the `patient\_name` bug). This is the first of a planned family of
 * jinja node types; the shared token regex lives in `./tokenRegex`, and
 * `jinjaNodePlugins` is the single registration array meant to grow as siblings
 * (e.g. `jinjaBlock`) are added.
 *
 * Pieces:
 *  - `remarkJinjaToken` — post-parse mdast transformer splitting text nodes on
 *    token matches (leaf `code`/`inlineCode` nodes are skipped automatically).
 *  - `jinjaTokenSchema` — the inline content node schema (parse/serialize).
 *  - `jinjaTokenStringifyHandlers` — remark-stringify handler emitting the raw
 *    text VERBATIM (no `state.safe()`, zero escapes).
 *  - `jinjaTokenNodeView` — minimal nodeView whose only job is the kind class.
 *  - `jinjaTokenInputRule` — live-typed tokens become nodes on close.
 *  - `jinjaTokenEnterKeymap` — Enter/Shift-Enter inside a token exits it.
 *  - `jinjaTokenNormalizer` — keeps tokens well-formed as the user edits
 *    (mark strip, absorption split, deferred dissolve/delete, tokenize, IME).
 */

/** The custom mdast node type this module round-trips through remark. */
export const MDAST_JINJA_TOKEN = 'jinjaToken'

interface JinjaTokenMdastNode extends MarkdownNode {
  type: typeof MDAST_JINJA_TOKEN
  value: string
}

function isJinjaTokenMdastNode(
  node: MarkdownNode
): node is JinjaTokenMdastNode {
  return node.type === MDAST_JINJA_TOKEN && typeof node.value === 'string'
}

/**
 * Post-parse mdast transformer: walk the tree, split `text` nodes on token
 * matches into `[text, jinjaToken, text, ...]`. Only `text` nodes are split, so
 * leaf `code` (fenced) / `inlineCode` mdast nodes are never touched and braces
 * inside code stay literal automatically.
 */
export const remarkJinjaToken = $remark('remarkJinjaToken', () => () => {
  const tokenRe = createTokenRegex()

  const visit = (node: MarkdownNode): void => {
    const children = node.children
    if (!children) return

    const next: MarkdownNode[] = []
    for (const child of children) {
      if (child.type !== 'text' || typeof child.value !== 'string') {
        visit(child)
        next.push(child)
        continue
      }

      const text = child.value
      tokenRe.lastIndex = 0
      let lastIndex = 0
      let match: RegExpExecArray | null
      let didSplit = false

      while ((match = tokenRe.exec(text)) !== null) {
        didSplit = true
        const start = match.index
        const end = start + match[0].length
        if (start > lastIndex) {
          next.push({ type: 'text', value: text.slice(lastIndex, start) })
        }
        const tokenNode: JinjaTokenMdastNode = {
          type: MDAST_JINJA_TOKEN,
          value: match[0],
        }
        next.push(tokenNode)
        lastIndex = end
      }

      if (!didSplit) {
        next.push(child)
        continue
      }
      if (lastIndex < text.length) {
        next.push({ type: 'text', value: text.slice(lastIndex) })
      }
    }

    node.children = next
  }

  return (tree) => {
    visit(tree as MarkdownNode)
  }
})

export const jinjaTokenSchema = $nodeSchema(MDAST_JINJA_TOKEN, () => ({
  inline: true,
  group: 'inline',
  // Inline CONTENT node: the token's full text — delimiters included — IS the
  // node content, so editing feels like normal text. `text*` (not `+`) so
  // transient empty states while typing are legal.
  content: 'text*',
  // Schema-level ban on marks INSIDE the token: its text must never carry
  // bold/italic (would serialize as `**{{ x }}**`).
  marks: '',
  // Preserve `\n` in a multiline `{% %}` when the DOM is re-parsed.
  whitespace: 'pre',
  selectable: true,
  // Content comes from the element's text.
  parseDOM: [{ tag: 'span[data-jinja-token]' }],
  // `0` is the content hole. The class serves static renders (snippet
  // preview); the nodeView keeps it current while editing.
  toDOM: (node) => [
    'span',
    { class: jinjaTokenClass(node.textContent), 'data-jinja-token': '' },
    0,
  ],
  parseMarkdown: {
    match: (node) => node.type === MDAST_JINJA_TOKEN,
    runner: (state, node, type) => {
      // Build the node with CLEAN, UNMARKED text: create the text child
      // explicitly rather than openNode+addText, which would inherit the active
      // parser mark set and corrupt e.g. `**before {{ x }} after**`.
      const value = isJinjaTokenMdastNode(node) ? node.value : ''
      state.addNode(
        type,
        undefined,
        value ? [state.schema.text(value)] : undefined
      )
    },
  },
  toMarkdown: {
    match: (node) => node.type.name === MDAST_JINJA_TOKEN,
    runner: (state, node) => {
      const text = node.textContent
      // Drop an abandoned empty/shell pill here rather than relying on the
      // normalizer's caret-leave/blur cleanup having landed first: Save reads
      // the last emitted markdown, so serialize is the only point guaranteed to
      // run after every edit. `{{ }}` would be a Jinja syntax error at render.
      if (text === '' || isDelimiterShell(text)) return
      // Emit a CUSTOM mdast node carrying the node's text VERBATIM (read from
      // textContent now); the stringify handler below returns it untouched.
      // Emitting a `text` node here would let remark-stringify's default text
      // handler re-escape `_ [ ] *` etc. (the original `patient\_name` bug).
      state.addNode(MDAST_JINJA_TOKEN, undefined, text)
    },
  },
}))

/**
 * remark-stringify handler keyed by the custom `jinjaToken` mdast node type:
 * returns the stored value verbatim. This is the layer that prevents
 * remark-stringify from re-escaping markdown-special chars (`_ [ ] *` etc.)
 * inside the token.
 *
 * `Options['handlers']` is keyed by the KNOWN mdast node union, which does not
 * include our custom `jinjaToken` type, so we widen the KEY to `string` via
 * `Record`. The VALUE is typed as remark-stringify's own handler type, so if
 * the library ever tightens that signature this stops compiling instead of
 * silently shipping a stale impl.
 */
type StringifyHandle = NonNullable<
  NonNullable<RemarkStringifyOptions['handlers']>[keyof NonNullable<
    RemarkStringifyOptions['handlers']
  >]
>

const stringifyJinjaToken: StringifyHandle = (node) => {
  const value = (node as { value?: unknown }).value
  return typeof value === 'string' ? value : ''
}

export const jinjaTokenStringifyHandlers: Record<string, StringifyHandle> = {
  [MDAST_JINJA_TOKEN]: stringifyJinjaToken,
}

/**
 * Minimal nodeView: the token text lives in the node's content (PM manages it
 * directly via `contentDOM`), so this view's ONLY job is the kind class
 * (`--fact` vs `--jinja`), which derives from `textContent`. PM won't re-run
 * `toDOM` on content-only edits, so `update` recomputes the class when a fact
 * token flips to a jinja one (e.g. `{foo}` → `{{ foo }}`) and the color follows.
 */
export const jinjaTokenNodeView = $view(
  jinjaTokenSchema.node,
  (): NodeViewConstructor => (initialNode) => {
    const dom = document.createElement('span')
    dom.className = jinjaTokenClass(initialNode.textContent)
    dom.setAttribute('data-jinja-token', '')
    // dom === contentDOM is allowed: PM edits the token text as this span's
    // children, so typing inside the token is ordinary text editing.
    return {
      dom,
      contentDOM: dom,
      update: (updated: ProseNode) => {
        if (updated.type.name !== MDAST_JINJA_TOKEN) return false
        const cls = jinjaTokenClass(updated.textContent)
        if (cls !== dom.className) dom.className = cls
        // Don't rebuild — PM reconciles the contentDOM text itself.
        return true
      },
    }
  }
)

/** True if any mark spanning the matched range is a code mark (`spec.code`). */
export function rangeHasCodeMark(
  state: EditorState,
  start: number,
  end: number
): boolean {
  let found = false
  state.doc.nodesBetween(start, Math.max(start, end - 1), (node) => {
    if (node.isInline && node.marks.some((m) => m.type.spec.code)) found = true
  })
  return found
}

/**
 * Replace `[start, end)` with a jinjaToken node whose text IS `value`, landing a
 * COLLAPSED caret just AFTER the node so typing continues as prose. Shared by
 * the input rule and the Facts autocomplete so the caret/marks contract lives in
 * one place: `setSelection` clears storedMarks, so bold typed BEFORE the token
 * would be lost on the char AFTER it — reinstate them.
 */
export function replaceWithJinjaToken(
  state: EditorState,
  type: NodeType,
  start: number,
  end: number,
  value: string
): Transaction {
  const marks = state.storedMarks ?? state.selection.$from.marks()
  const node = type.create(null, state.schema.text(value))
  const tr = state.tr.replaceWith(start, end, node)
  tr.setSelection(TextSelection.create(tr.doc, start + node.nodeSize))
  tr.setStoredMarks(marks)
  return tr
}

/**
 * Input rule: when the user finishes typing a token (the closing delimiter is
 * the last char before the cursor), replace the literal text with a jinjaToken
 * CONTENT node whose text IS that literal token, then land the caret just AFTER
 * the node so typing continues as prose. A single undo right after conversion
 * restores the plain literal text (native `InputRule` undo).
 *
 * KNOWN deferred issue — text typed immediately after the token may be absorbed
 * into its content by Chromium's contentEditable; the mark guard + the
 * caret-after placement in `replaceWithJinjaToken` are enough for the prototype.
 *
 * Milkdown's `customInputRules` only skips code BLOCKS, not inline-code MARKS,
 * so the handler bails when the match sits under a code mark, keeping live-typed
 * tokens inside an inline-code span literal (parity with the mdast transformer).
 */
export const jinjaTokenInputRule = $inputRule(
  (ctx: Ctx) =>
    new InputRule(createInputRuleRegex(), (state, match, start, end) => {
      const value = match[0]
      if (!value) return null
      // Already inside a token → don't nest a token inside a token.
      if (state.selection.$from.parent.type.name === MDAST_JINJA_TOKEN)
        return null
      if (rangeHasCodeMark(state, start, end)) return null
      return replaceWithJinjaToken(
        state,
        jinjaTokenSchema.type(ctx),
        start,
        end,
        value
      )
    })
)

/**
 * Enter (and Shift-Enter) inside a token exits it — moves the caret to just
 * after the enclosing token node — instead of splitting the token. `content:
 * 'text*'` rejects a hardbreak, so Shift-Enter is consumed too, without
 * inserting a newline. Returns false when the caret is not inside a token, so
 * normal Enter typing is untouched.
 */
export const exitJinjaTokenOnEnter: Command = (state, dispatch) => {
  // Only a collapsed caret inside a token exits it; a range selection (even one
  // starting inside a token) falls through to the default Enter behavior.
  if (!state.selection.empty) return false
  const { $from } = state.selection
  if ($from.parent.type.name !== MDAST_JINJA_TOKEN) return false
  if (dispatch) {
    dispatch(
      state.tr.setSelection(TextSelection.create(state.doc, $from.after()))
    )
  }
  return true
}

export const jinjaTokenEnterKeymap = $prose(() =>
  keymap({ Enter: exitJinjaTokenOnEnter, 'Shift-Enter': exitJinjaTokenOnEnter })
)

/**
 * Single registration array for the jinja node family. Each composable expands
 * to a tuple of plugins, so the array is flattened. Sibling jinja node types
 * (e.g. a future `jinjaBlock`) append their plugins here.
 */
export const jinjaNodePlugins = [
  remarkJinjaToken,
  jinjaTokenSchema,
  jinjaTokenNodeView,
  jinjaTokenInputRule,
  jinjaTokenEnterKeymap,
  jinjaTokenNormalizer,
].flat()

/**
 * Register the whole jinja node family on a Crepe editor: wire the verbatim
 * stringify handler, then load the node plugins. Call BEFORE `crepe.create()`.
 * Keeps the editor wiring to one line and the config detail owned by the module.
 */
export function registerJinjaNodes(
  editor: Editor,
  snippetConfig?: SnippetBlockViewConfig
): void {
  editor.config((ctx) => {
    ctx.set(remarkStringifyOptionsCtx, {
      handlers: snippetConfig
        ? {
            ...jinjaTokenStringifyHandlers,
            ...snippetBlockStringifyHandlers,
          }
        : jinjaTokenStringifyHandlers,
    })
    if (snippetConfig) ctx.set(snippetBlockViewConfig.key, snippetConfig)
  })
  if (snippetConfig) editor.use(snippetBlockPlugins)
  editor.use(jinjaNodePlugins)
}
